import { HOURS_PER_DAY, MINUTES_PER_DAY } from '../model/constants';
import { addDays, daysBetween, formatDate, parseDate } from './date';

/**
 * Personal-day helpers. A personal day starts at `dayStartMin` (minutes after local
 * midnight) and lasts 24 hours; wall minutes are counted from 00:00 of the personal
 * date's calendar day and can exceed 1440 (docs/glossary.md).
 * All conversions use the local wall clock of the running environment (browser time zone).
 */

/** Calendar date (local wall clock) of an instant. */
export function localCalendarDate(epochMs: number): string {
  const local = new Date(epochMs);
  return formatDate({
    year: local.getFullYear(),
    month: local.getMonth() + 1,
    day: local.getDate(),
  });
}

/** Minutes after local midnight of an instant. */
export function localMinuteOfDay(epochMs: number): number {
  const local = new Date(epochMs);
  return local.getHours() * 60 + local.getMinutes();
}

/** The personal date an instant belongs to. */
export function personalDateOf(epochMs: number, dayStartMin: number): string {
  const calendarDate = localCalendarDate(epochMs);
  return localMinuteOfDay(epochMs) < dayStartMin ? addDays(calendarDate, -1) : calendarDate;
}

/** Wall minutes of an instant counted from 00:00 of `personalDate` (can exceed 1440). */
export function wallMinOf(epochMs: number, personalDate: string): number {
  const dayOffset = daysBetween(personalDate, localCalendarDate(epochMs));
  return dayOffset * MINUTES_PER_DAY + localMinuteOfDay(epochMs);
}

/** The instant (local wall clock) at `wallMin` minutes after 00:00 of `personalDate`. */
export function epochOfWallMin(personalDate: string, wallMin: number): number {
  const { year, month, day } = parseDate(personalDate);
  return new Date(year, month - 1, day, 0, wallMin).getTime();
}

/** "HH:MM" of a wall-minute value, wrapped into one day (1440 becomes "00:00"). */
export function formatMin(min: number): string {
  const wrapped = ((Math.round(min) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(wrapped / 60);
  const minutes = wrapped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Wall-clock hour bucket (0-23) of a wall-minute value. */
export function wallHourOf(min: number): number {
  const wrapped = ((min % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return Math.floor(wrapped / 60) % HOURS_PER_DAY;
}
